import { useCallback, useEffect, useRef } from 'react';
import { Cookies } from 'react-cookie';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';

import { useTypedSelector } from '../../../shared/hooks';
import AlertService from '../../../shared/services/AlertService';
import { alertQueueActions } from '../../../shared/store/alertQueueSlice';
import { type RootState } from '../../../shared/types';
import {
  DEFAULT_EXPORT_POLLING_INTERVAL_MS,
  resolveExportPollingIntervalMs,
} from '../../../shared/utils/exportPollingInterval';
import { licenseExportActions } from '../data-layer/licenseExportSlice';
import { LICENSE_INTELLIGENCE_EXPORT_STATUS_RESPONSE } from '../mock-data/licenseIntelligenceSimulationExportData';
import type {
  LicenseExportStatusData,
  LicenseExportStatusItem,
} from '../types';

/**
 * Reads the HTTP status from an RTK Query / axios base-query error, which
 * exposes `status` as a number (e.g. `{ status: 403, data: ... }`).
 */
const getQueryErrorHttpStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const { status } = error as { status?: unknown };

  return typeof status === 'number' ? status : undefined;
};

/**
 * Hook to poll the license export status endpoint and dispatch state updates.
 * Mirrors ViolationsReport's `useExportStatusPolling` but simplified
 * for a single export (no reportType multiplexing).
 */
const useLicenseExportStatusPolling = () => {
  const dispatch = useDispatch();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollRef = useRef<() => Promise<void>>(async () => {});
  const shownFailureBannerRef = useRef(false);
  const shownSuccessBannerRef = useRef(false);

  const { activeExport } = useTypedSelector(
    (state: RootState) => state.aag.licenseExport ?? { activeExport: null }
  );
  // Mock export status query
  const getExportStatus = useCallback(
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    (_userId: string) => ({
      unwrap: () =>
        Promise.resolve(LICENSE_INTELLIGENCE_EXPORT_STATUS_RESPONSE),
    }),
    []
  );

  const getCurrentUserId = useCallback(() => {
    const cookies = new Cookies();

    return cookies.get<string>('user_name');
  }, []);

  const showErrorBanner = useCallback(
    (errorMessage?: string) => {
      if (shownFailureBannerRef.current) {
        return;
      }

      shownFailureBannerRef.current = true;

      const description =
        errorMessage ?? 'License export has failed. Please try again.';

      dispatch(
        alertQueueActions.enqueue(
          AlertService.error(
            'Export failed',
            description,
            5000,
            `license-export-poll-error-${Date.now()}`
          )
        )
      );
    },
    [dispatch]
  );

  const stopPolling = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    dispatch(licenseExportActions.setIsPolling(false));
    shownFailureBannerRef.current = false;
    shownSuccessBannerRef.current = false;
  }, [dispatch]);

  const scheduleNextPoll = useCallback((delayMs: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      pollRef
        .current()
        .catch((error) =>
          Logger.log('error', 'License export polling failed:', error)
        );
    }, delayMs);
  }, []);

  const pollExportStatus = useCallback(async () => {
    try {
      const userId = getCurrentUserId();
      const response: LicenseExportStatusData = await getExportStatus(
        userId
      ).unwrap();

      // Process each export in the response
      const inProgressExports: LicenseExportStatusItem[] = [];

      response.exports.forEach((exportItem: LicenseExportStatusItem) => {
        const { status, progressPercentage, exportId, errorMessage } =
          exportItem;

        // Update progress
        dispatch(licenseExportActions.setExportProgress(progressPercentage));

        if (status === 'COMPLETED') {
          dispatch(licenseExportActions.setExportId(exportId));
          dispatch(licenseExportActions.setExportStatus('COMPLETED'));
          dispatch(licenseExportActions.removeActiveExport());
          dispatch(
            licenseExportActions.setCompletedExportsCount(
              response.completedExports
            )
          );
        } else if (status === 'FAILED') {
          showErrorBanner(errorMessage);
          dispatch(licenseExportActions.setExportStatus('FAILED'));

          if (exportId) {
            dispatch(licenseExportActions.setExportId(exportId));
          }

          dispatch(licenseExportActions.removeActiveExport());
        } else {
          // QUEUED or PROCESSING
          inProgressExports.push(exportItem);
        }
      });

      // Decide whether to continue polling
      const shouldStopPolling =
        response.inProgressExports === 0 || inProgressExports.length === 0;

      if (shouldStopPolling) {
        dispatch(licenseExportActions.removeActiveExport());
        stopPolling();
      } else {
        scheduleNextPoll(resolveExportPollingIntervalMs(inProgressExports));
      }

      // Clear completed data if none
      if (response.completedExports === 0) {
        dispatch(licenseExportActions.clearCompletedExport());
      }

      dispatch(
        licenseExportActions.setCompletedExportsCount(response.completedExports)
      );
    } catch (error) {
      Logger.log('error', 'Failed to poll license export status:', error);

      // Auth failures (401 Unauthorized / 403 Forbidden) are not transient —
      // retrying will never succeed and would loop indefinitely. Stop polling.
      const httpStatus = getQueryErrorHttpStatus(error);

      if (httpStatus === 401 || httpStatus === 403) {
        stopPolling();

        return;
      }

      scheduleNextPoll(DEFAULT_EXPORT_POLLING_INTERVAL_MS);
    }
  }, [
    getCurrentUserId,
    getExportStatus,
    showErrorBanner,
    dispatch,
    stopPolling,
    scheduleNextPoll,
  ]);

  pollRef.current = pollExportStatus;

  const startPolling = useCallback(() => {
    if (timeoutRef.current) {
      return; // Already polling
    }

    scheduleNextPoll(DEFAULT_EXPORT_POLLING_INTERVAL_MS);
  }, [scheduleNextPoll]);

  // Check for existing in-progress exports on initial mount only
  useEffect(() => {
    // Skip initial check if an export was just triggered by the user
    if (activeExport) {
      return;
    }

    const checkInitialExports = async () => {
      try {
        const userId = getCurrentUserId();
        const response: LicenseExportStatusData = await getExportStatus(
          userId
        ).unwrap();

        dispatch(
          licenseExportActions.setCompletedExportsCount(
            response.completedExports
          )
        );

        if (response.completedExports === 0) {
          dispatch(licenseExportActions.clearCompletedExport());
        }

        const inProgressExports = response.exports.filter(
          (exportItem: LicenseExportStatusItem) =>
            exportItem.status === 'PROCESSING' || exportItem.status === 'QUEUED'
        );

        if (inProgressExports.length > 0) {
          dispatch(
            licenseExportActions.setActiveExport({
              progress: inProgressExports[0].progressPercentage,
            })
          );
        }
      } catch (error) {
        Logger.log('error', 'Failed to check initial license exports:', error);
      }
    };

    checkInitialExports().catch((error) =>
      Logger.log('error', 'Initial license export check failed:', error)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start/stop polling based on activeExport from Redux
  useEffect(() => {
    if (activeExport) {
      dispatch(licenseExportActions.setIsPolling(true));
      startPolling();
    } else {
      stopPolling();
    }
  }, [activeExport, startPolling, stopPolling, dispatch]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );
};

export default useLicenseExportStatusPolling;
